<script module>
  export const i18n = {
    sv: {
      '%s to %s': '%s till %s',
      'Could not create booking': 'Kunde inte skapa bokning'
    }
  }
</script>

<script>
  import { goto, preloadCode } from '$app/navigation'
  import { enhance } from '$app/forms'
  import { page } from '$app/state'
  import { onMount } from 'svelte'

  import { getDefaultDate } from '$lib/Datepicker.svelte'
  import { showNewMessage } from '$lib/Intercom.svelte'
  import { register } from '$lib/utils/text.svelte'
  import { track } from '$lib/plausible.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import { prefix } from '$lib/utils/prefix.js'
  import Journeys from '$lib/Journeys.svelte'
  import SearchBar from './SearchBar.svelte'
  import { resolve } from '$lib/prismic.js'
  import Header from '$lib/Header.svelte'
  import Footer from '$lib/Footer.svelte'
  import Config from '$lib/Config.svelte'
  import Sidebar from './Sidebar.svelte'
  import Modal from '$lib/Modal.svelte'
  import Html from '$lib/Html.svelte'
  import Meta from '$lib/Meta.svelte'
  import {
    passengersFromParams,
    passengersToParams
  } from '$lib/utils/passengers.js'

  let text = register(i18n)

  /** @type {{ children?: Svelte.Snippet }} */
  let { children } = $props()

  const getDuration = () => {
    const duration = page.url.searchParams.get('duration')
    if (!duration) return 'P1D'
    if (!isNaN(+duration)) return `P${duration}D`
    return duration
  }

  const data = /** @type {import('./$types').PageData} */ (page.data)

  /** @type {Schema.JourneyOffer|undefined} */
  let selectedJourneyOffer = $state()

  /** @type {App.ActionFailure|undefined}*/
  let error = $state()

  let isLoading = $state(false)

  /** @type {Schema.Location?} */
  let origin = $derived(data.origin)

  /** @type {Schema.Location?} */
  let destination = $derived(data.destination)

  /** @type {Schema.Location?} */
  let via = $derived(data.via)

  let duration = $derived(getDuration())
  let passengers = $derived(
    passengersFromParams(page.url.searchParams) || [
      { type: /** @type {Schema.PassengerType} */ ('ADULT') }
    ]
  )
  let departureDate = $derived(
    page.url.searchParams.get('ddate') || getDefaultDate()
  )

  let params = $state({
    origin: data.origin,
    destination: data.destination,
    via: data.via,
    duration: getDuration(),
    departureDate: page.url.searchParams.get('ddate') || getDefaultDate(),
    passengers: passengersFromParams(page.url.searchParams) || [
      { type: /** @type {Schema.PassengerType} */ ('ADULT') }
    ]
  })

  let steps = $derived([
    { name: 'planning', selected: true },
    { name: 'tickets' },
    { name: 'passengers' },
    { name: data.agent.paymentMethod === 'STRIPE' ? 'payment' : 'order' }
  ])

  let languages = $derived(
    data.content?.alternate_languages.length
      ? [data.content, ...data.content.alternate_languages]
          .map((doc) => {
            const href = resolve(doc)
            return href ? { href, lang: doc.lang } : null
          })
          .filter(Boolean)
      : []
  )

  $effect(() => {
    if (selectedJourneyOffer) {
      track('Select Journey Offer')
    }
  })

  onMount(() => {
    preloadCode(`${$prefix}/booking/__booking__/tickets`)
  })

  const onsearch = (event) => {
    const data = new FormData(event.currentTarget)

    // @ts-ignore
    const query = new URLSearchParams(data)

    goto(`${$prefix}/search?${query}`, {
      replaceState: true,
      keepFocus: true,
      noScroll: true
    })

    selectedJourneyOffer = undefined
    params = {
      origin,
      destination,
      via,
      duration,
      departureDate,
      passengers
    }

    event.preventDefault()
  }

  /** @type {import('@sveltejs/kit').SubmitFunction<{ booking: Schema.Booking }, App.ActionFailure>} */
  const onbook = async ({ cancel }) => {
    if (isLoading || !selectedJourneyOffer) return cancel()
    isLoading = true

    track('Begin Checkout', {
      props: { product: 'journey' }
    })

    return ({ result, update }) => {
      isLoading = result.type === 'redirect'
      if (result.type === 'failure') {
        error = result.data
        track('Exception', {
          props: { message: error?.message }
        })
      } else {
        update()
      }
    }
  }
</script>

{#snippet form()}
  {#if origin}
    <input type="hidden" name="origin" value={origin.uid} />
  {/if}
  {#if destination}
    <input type="hidden" name="destination" value={destination.uid} />
  {/if}
  {#if via}
    <input type="hidden" name="via" value={via.uid} />
  {/if}
  <input type="hidden" name="duration" value={duration} />
  <input type="hidden" name="ddate" value={departureDate} />
  {#if passengers.length}
    {#each passengersToParams(passengers) as value}
      <input type="hidden" name="passenger" {value} />
    {/each}
  {/if}
{/snippet}

<Meta title={text`${data.origin?.name} to ${data.destination?.name}`} />

<Config
  primary={data.agent.settings?.data.primary_color}
  secondary={data.agent.settings?.data.secondary_color}>
  <div class="journey-search">
    <Header {languages} {steps} navigation={false} currencies />

    {#if error}
      <Modal onclose={() => (error = undefined)}>
        <Html>
          <h2>{text`Could not create booking`}</h2>
          <p>{text(error.message)}</p>
        </Html>
      </Modal>
    {/if}

    <form autocomplete="off" autocapitalize="off" onsubmit={onsearch}>
      <SearchBar
        bind:origin
        bind:destination
        bind:via
        bind:duration
        bind:departureDate
        bind:passengers />
      {@render form()}
    </form>

    <div class="layout theme-smoke">
      <form
        class="main theme-reset"
        onsubmit={(event) => event.preventDefault()}>
        {#if children}
          {@render children()}
        {:else}
          <Journeys
            splash
            {...params}
            disabled={isLoading}
            blueprint={page.url.searchParams.get('blueprint')}
            onselect={(journeyOffer) => {
              selectedJourneyOffer = journeyOffer
            }}>
            {#snippet error(message)}
              <div class="error theme-reset">
                <Html size="small">
                  <h2 class="h3">{text`No journeys found`}</h2>
                  <p>
                    {text`We're having trouble finding any trains right now.`}
                    {@html message}
                  </p>
                  <p>
                    {text`If the problem persists, please`}
                    <a
                      href="mailto:support@allaboard.eu"
                      onclick={(event) => {
                        showNewMessage()
                        event.preventDefault()
                      }}>
                      {text`send us a message`}
                    </a>
                  </p>
                </Html>
              </div>
            {/snippet}
          </Journeys>
        {/if}
        {@render form()}
      </form>

      {#if origin && destination}
        <form
          method="POST"
          action="{$prefix}/booking"
          class="sidebar theme-reset"
          use:enhance={onbook}>
          <Sidebar
            {...params}
            loading={isLoading}
            journeyOffer={selectedJourneyOffer} />
          <input type="hidden" name="currency" value={State.currency} />
        </form>
      {/if}
    </div>

    <Footer internal />
  </div>
</Config>

<style>
  .journey-search {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: var(--background);
  }

  .layout {
    --horizontal-padding: max(
      (100vw - var(--container-large-width)) / 2,
      var(--document-margin)
    );

    flex: 1 0 auto;
    display: flex;
    align-items: start;
    flex-wrap: wrap;
    gap: 2rem;
    padding-top: 1.5rem;
    background: var(--background);
    color: rgb(var(--rgb-text));
  }

  @media (width >= 1000px) {
    .layout {
      flex-wrap: nowrap;
      gap: clamp(2rem, 3.5vw, 4rem);
      padding: 1.5rem var(--horizontal-padding) 0;
    }

    @supports (padding-left: env(safe-area-inset-left)) {
      .layout {
        padding-left: calc(
          var(--horizontal-padding) + env(safe-area-inset-left)
        );
        padding-right: calc(
          var(--horizontal-padding) + env(safe-area-inset-right)
        );
      }
    }
  }

  .main {
    min-width: 0;
    margin-bottom: clamp(2rem, 10vh, 5rem);
  }

  @media (width < 1000px) {
    .main {
      width: 100%;
      padding: 0 var(--horizontal-padding);
    }

    @supports (padding-left: env(safe-area-inset-left)) {
      .main {
        padding-left: calc(
          var(--horizontal-padding) + env(safe-area-inset-left)
        );
        padding-right: calc(
          var(--horizontal-padding) + env(safe-area-inset-right)
        );
      }
    }
  }

  @media (width > 1000px) {
    .main {
      flex: 1 1 auto;
    }
  }

  .sidebar {
    flex: 0 0 100%;
    padding: clamp(1rem, 1.5vw, 1.5rem) var(--document-margin);
    margin-top: auto;
    position: sticky;
    top: 1.5rem;
    color: rgb(var(--rgb-text));
    background: var(--background);
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
  }

  @supports (padding-left: env(safe-area-inset-left)) {
    .sidebar {
      padding-left: calc(var(--document-margin) + env(safe-area-inset-left));
      padding-right: calc(var(--document-margin) + env(safe-area-inset-right));
    }
  }

  @media (width >= 1000px) {
    .sidebar {
      flex: 0 0 25rem;
      padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.25rem, 2vw, 1.75rem);
      margin: 0;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-sm);
    }
  }
</style>
